import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AutoSizer, Column, SortDirection, Table } from "react-virtualized";
import { Button, Modal } from 'react-bootstrap';
import { FaFileDownload, FaPoundSign, FaStar } from "react-icons/fa";
import { MdDownload, MdEdit, MdVisibility } from "react-icons/md";
import debounce from 'lodash.debounce';
import LoadingSpinner from "../Loading-Component/Loading";
import { formatShortDate, CustomTooltip } from '../../utils/helpers';
import QueryForm from '../QueryForm-Component/QueryForm';
import Select from "react-select";
import CustomToast from "../Toast-Component/CustomToast";
import { isMobileOnly } from 'react-device-detect';
import QueryTableCard from "./QueryTableCard"
import "./QueryTableCard.scss"
import { authenticatedFetch } from '../../utils/api';
import ColumnToggle from '../shared/ColumnToggle';

const QueryTable = ({ queryIdToEdit, setQueryIdToEdit, preloadedData = null, loading, setLoading, filters: filtersProp, setFilters: setFiltersProp, onResolutionTypesLoaded, toggleStatus: toggleStatusProp, setToggleStatus: setToggleStatusProp, visibleColumns, columnConfig, onColumnsChange }) => {
    const [queryData, setQueryData] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(!!preloadedData);
    const [sortBy, setSortBy] = useState('dateLogged');
    const [sortDirection, setSortDirection] = useState(SortDirection.DESC);
    const [showModal, setShowModal] = useState(false);
    const [downloadLinks, setDownloadLinks] = useState([]);
    const [showQueryFormPopup, setShowQueryFormPopup] = useState(false)
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [internalToggleStatus, setInternalToggleStatus] = useState('open');
    const toggleStatus = toggleStatusProp !== undefined ? toggleStatusProp : internalToggleStatus;
    const setToggleStatus = setToggleStatusProp || setInternalToggleStatus;
    const [toastProps, setToastProps] = useState({
        title: '',
        bodyText: '',
        status: 'default',
        delay: 3000,
        autohide: true,
        position: 'top-end',
    });
    const isMobile = isMobileOnly;

    const [internalSearchTerm, setInternalSearchTerm] = useState('');
    const [internalRoleDepartmentFilter, setInternalRoleDepartmentFilter] = useState('');
    const [internalResolutionTypeFilter, setInternalResolutionTypeFilter] = useState('');
    const [internalSupplierFilter, setInternalSupplierFilter] = useState('');

    const searchTerm = filtersProp ? filtersProp.searchTerm : internalSearchTerm;
    const setSearchTerm = filtersProp ? (val) => setFiltersProp(prev => ({ ...prev, searchTerm: val })) : setInternalSearchTerm;
    const roleDepartmentFilter = filtersProp ? filtersProp.roleDepartment : internalRoleDepartmentFilter;
    const setRoleDepartmentFilter = filtersProp ? (val) => setFiltersProp(prev => ({ ...prev, roleDepartment: val })) : setInternalRoleDepartmentFilter;
    const resolutionTypeFilter = filtersProp ? filtersProp.resolutionType : internalResolutionTypeFilter;
    const setResolutionTypeFilter = filtersProp ? (val) => setFiltersProp(prev => ({ ...prev, resolutionType: val })) : setInternalResolutionTypeFilter;
    const supplierFilter = filtersProp ? filtersProp.supplier : internalSupplierFilter;
    const setSupplierFilter = filtersProp ? (val) => setFiltersProp(prev => ({ ...prev, supplier: val })) : setInternalSupplierFilter;

    const [resolutionTypes, setResolutionTypes] = useState([]);

    async function getQueryTable() {
        if (dataLoaded && queryData.length > 0) return;

        setLoading(true);

        try {
            const res = await authenticatedFetch('/ManagerReport/GetSubmittedQueries', {
                method: 'POST',
                body: JSON.stringify({}),
            });
            const result = await res.json();
            if (result.message != null) {
                setLoading(false);
                window.alert(result.item2);
                return;
            }
            setQueryData(result.item1 || []);
            setDataLoaded(true);
            setLoading(false);
        }
        catch (ex) {
            console.log(ex);
            setLoading(false);
        }
    }

    async function getResolutionTypes() {
        try {
            const resolutionTypesResponse = await authenticatedFetch('/ManagerReport/GetResolutionTypes', {
                method: 'GET',
            });

            const response = await resolutionTypesResponse.json();
            const types = response.resolutionTypes || [];
            setResolutionTypes(types);
            onResolutionTypesLoaded?.(types);
        }
        catch (ex) {
            console.log('Error fetching resolution types:', ex);
        }
    }

    useEffect(() => {
        if (!dataLoaded) {
            getQueryTable();
        }
        getResolutionTypes();
    }, []);

    useEffect(() => {
        if (preloadedData && preloadedData.length > 0) {
            setQueryData(preloadedData);
            setDataLoaded(true);
            setLoading(false);
        }
    }, [preloadedData]);

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                setSearchTerm(value);
            }, 300),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleSearchChange = useCallback(
        (e) => {
            const { value } = e.target;
            debouncedSearch(value);
        },
        [debouncedSearch]
    );

    const filteredData = useMemo(() => {
        if (!searchTerm) return queryData;
        const lowercasedTerm = searchTerm.toLowerCase();
        return queryData.filter((item) => {
            return (
                (item.id && item.id.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.lineID && item.lineID.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.siteref && item.siteref.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.providerName && item.providerName.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.queryContent && item.queryContent.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.raisedby && item.raisedby.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.querytype && item.querytype.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.roledepartment && item.roledepartment.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.sitename && item.sitename.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.rolename && item.rolename.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.credited && item.credited.toString().toLowerCase().includes(lowercasedTerm)) ||
                (item.resolutionContent && item.resolutionContent.toString().toLowerCase().includes(lowercasedTerm))
            );
        });
    }, [queryData, searchTerm]);

    const roleFilteredData = useMemo(() => {
        if (!roleDepartmentFilter) return filteredData;
        return filteredData.filter((item) =>
            item.roledepartment && item.roledepartment.toLowerCase() === roleDepartmentFilter.toLowerCase()
        );
    }, [filteredData, roleDepartmentFilter]);

    const resolutionFilteredData = useMemo(() => {
        if (!resolutionTypeFilter) return roleFilteredData;

        return roleFilteredData.filter((item) =>
            item.resolutionID && item.resolutionID.toString() === resolutionTypeFilter.toString()
        );
    }, [roleFilteredData, resolutionTypeFilter]);

    const supplierFilteredData = useMemo(() => {
        if (!supplierFilter) return resolutionFilteredData;
        return resolutionFilteredData.filter((item) =>
            item.providerName && item.providerName.toLowerCase() === supplierFilter.toLowerCase()
        );
    }, [resolutionFilteredData, supplierFilter]);

    const handleSort = ({ sortBy: newSortBy }) => {
        let newSortDirection = SortDirection.ASC;
        if (sortBy === newSortBy) {
            newSortDirection =
                sortDirection === SortDirection.ASC
                    ? SortDirection.DESC
                    : SortDirection.ASC;
        }
        setSortBy(newSortBy);
        setSortDirection(newSortDirection);
    };

    const areBlobNamesIncludedInDownloadLinks = (blobNames, downloadLinks) => {
        if (blobNames.length !== downloadLinks.length) {
            console.warn("Arrays have different lengths.");
            return false;
        }
        for (let i = 0; i < blobNames.length; i++) {
            const blobName = blobNames[i];
            const downloadItem = downloadLinks[i];
            if (!downloadItem?.fileName || downloadItem.fileName !== blobName) {
                console.warn(`Blob name "${blobName}" not found in download link at index ${i}.`);
                return false;
            }
        }
        return true;
    }

    const sortedData = useMemo(() => {
        if (!sortBy) return supplierFilteredData;
        const sorted = [...supplierFilteredData].sort((a, b) => {
            const aValue = a[sortBy];
            const bValue = b[sortBy];
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return aValue - bValue;
            }
            return aValue.toString().localeCompare(bValue.toString());
        });
        return sortDirection === SortDirection.ASC ? sorted : sorted.reverse();
    }, [supplierFilteredData, sortBy, sortDirection]);

    const uniqueRoleDepartments = useMemo(() => {
        const departments = [...new Set(queryData.map(item => item.roledepartment).filter(Boolean))];
        return departments.sort();
    }, [queryData]);

    const uniqueSuppliers = useMemo(() => {
        const suppliers = [...new Set(queryData.map(item => item.providerName).filter(Boolean))];
        return suppliers.sort();
    }, [queryData]);

    const headerRenderer = ({ dataKey, label }) => {
        return (
            <div onClick={() => handleSort({ sortBy: dataKey })}>
                {label}
            </div>
        );
    };

    const defaultCellRenderer = ({ cellData, rowData }) => {
        return <div title={cellData}>{cellData}</div>;
    };

    const DownloadModal = ({ links, onClose }) => {
        const uRefBlobs = (selectedRowData?.uRefForAzureImages || '').split(',').filter(name => name.trim());
        const providerBlobs = (selectedRowData?.providerEvidence || '').split(',').filter(name => name.trim());
        const allBlobs = [...uRefBlobs, ...providerBlobs]; 

        return (
            <Modal show={showModal} onHide={onClose} size="lg" dialogClassName="modal-dialog-centered" className="query-modal evidence-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Evidence</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="model-table-container">
                        <table style={{ maxWidth: "340px" }}>
                            <thead>
                                <tr>
                                    <th><div className="table-cell">Name of document</div></th>
                                    <th><div className="table-cell">Date received</div></th>
                                    <th><div className="table-cell">File</div></th>
                                </tr>
                            </thead>
                            <tbody>
                                {links.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="table-cell">
                                                {item.fileName?.split('_')[0] || `Document ${index + 1}`}
                                                {!item.found && <span className="text-danger"> (Not Found)</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-cell">{formatShortDate(selectedRowData?.dateLogged)}</div>
                                        </td>
                                        <td>
                                            <div className="table-cell">
                                                {!item.found ? (
                                                    <span className="text-muted">N/A</span>
                                                ) : (
                                                    <a href={item.link} target="_blank" rel="noopener noreferrer" title="click to see evidence docs">
                                                        <FaFileDownload />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const handleDownloadClick = async (rowData) => {
        if (!rowData.uRefForAzureImages && !rowData.providerEvidence) return;
        const uRefBlobs = (rowData.uRefForAzureImages || '').split(',');
        const providerBlobs = (rowData.providerEvidence || '').split(',');
        const combinedBlobNames = [...uRefBlobs, ...providerBlobs]
            .map(name => name.trim())
            .filter(name => !!name);

        if (combinedBlobNames.length === 0) return;

        const blobNamesString = combinedBlobNames.join(',');

        try {
            setLoading(true);

            let activeLinks = downloadLinks;

            if (activeLinks.length === 0 || !areBlobNamesIncludedInDownloadLinks(combinedBlobNames, activeLinks)) {
                const queryParam = encodeURIComponent(blobNamesString);
                const response = await authenticatedFetch(`/Blob/download-links?blobNames=${queryParam}`, {
                    method: 'GET',
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    setToastProps({
                        title: 'Error',
                        bodyText: 'Something went wrong!',
                        status: 'error',
                        delay: 5000,
                        autohide: true,
                        position: 'top-end',
                    });
                    setLoading(false);
                    setShowToast(true);
                    throw new Error(`Server error: ${response.status} - ${errorText}`);
                }

                activeLinks = await response.json();
                setDownloadLinks(activeLinks);
            }

            if (activeLinks.length === 1 && activeLinks[0].found) {
                setLoading(false);
                window.open(activeLinks[0].link, '_blank');
            } else {
                setSelectedRowData(rowData);
                setShowModal(true);
                setLoading(false);
            }
        } catch (error) {
            console.error('Error fetching links:', error);
            setLoading(false);
        }
    };

    const downloadCellRenderer = ({ rowData }) => {
        if (rowData.uRefForAzureImages || rowData.providerEvidence) {
            return (
                <button className="btn btn-primary btn-table-action" onClick={() => handleDownloadClick(rowData)}>
                    <MdDownload />
                </button>

            );
        }
        return null;
    };

    const queryCategoryCellRenderer = ({ rowData }) => {
        const category = rowData.querycategory?.toLowerCase();
        if (category === 'billing') {
            return (
                <CustomTooltip content="Billing Query">
                    <FaPoundSign color="green" size={16} />
                </CustomTooltip>
            );
        } else if (category === 'quality') {
            return (
                <CustomTooltip content="Quality Query">
                    <FaStar color="orange" size={16} />
                </CustomTooltip>
            );
        }
        return null;
    };

    const rowHighlightStyle = (index) => {
        if (filteredToggledData.length > 0 && index >= 0) {
            const rowData = filteredToggledData[index];
            if (rowData.rowcolour) {
                return { backgroundColor: rowData.rowcolour, color: '#0c022f' };
            } else if (rowData.resolutionContent?.includes("Closed")) {
                return { backgroundColor: '#ddd', textDecoration: 'line-through' };
            } 
        }
    }

    const openQueryFormModal = (rowData) => {
        setSelectedRowData(rowData);
        setShowQueryFormPopup(true);
    };

    const QueryFormModal = ({ onClose }) => {

        const nettCharge = selectedRowData?.queryCorrectNett?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        }) || '0.00';

        const rateBilled = selectedRowData?.rateBilled?.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        }) || '0.00';

        const ReadOnlyData = {
            lineID: selectedRowData?.lineID,
            siteRef: selectedRowData?.siteref,
            siteName: selectedRowData?.sitename,
            dateOfJob: selectedRowData?.dateOfJob,
            attendedFrom: selectedRowData?.attendedfrom,
            attendedTo: selectedRowData?.attendedto,
            hrsBilled: selectedRowData?.hrsBilled,
            rateBilled: rateBilled,
            nettCharge: nettCharge,
            providerName: selectedRowData?.providerName,
            dateLogged: selectedRowData?.dateLogged,
            parentQuery: selectedRowData?.parentQuery,
            staffid: selectedRowData?.staffid,
            dateResolved: selectedRowData?.dateResolved,
            resolvedHrs: selectedRowData?.resolvedHrs,
            resolvedRate: selectedRowData?.resolvedRate,
            resolvedNett: selectedRowData?.resolvedNett,
            roleName: selectedRowData?.rolename,
            notes: selectedRowData?.notes
        };

        const UserData = {
            raisedBy: selectedRowData?.raisedby,
            jobTitle: selectedRowData?.jobtitle,
            contactNo: selectedRowData?.contactNo,
            raisedByEmail: selectedRowData?.raisedbyEmail
        };

        const existingQueryData = {
            QueryID: selectedRowData?.id,
            UpdateAdd: 0,
            QueryCategory: selectedRowData?.queryCategory_ID,
            QueryTypeID: selectedRowData?.querytypeID,
            QueryContent: selectedRowData?.queryContent,
            CorrectNett: selectedRowData?.queryCorrectNett,
            CorrectHours: selectedRowData?.queryCorrectHrs,
            CorrectRate: selectedRowData?.correctRate,
            CorrectStartTime: selectedRowData?.queryCorrectStart,
            CorrectEndTime: selectedRowData?.queryCorrectFinish,
            ResolutionType: selectedRowData?.resolutionType,
            ResolutionContent: selectedRowData?.resolutionContent,
            ResolutionSatisfaction: selectedRowData?.resolutionSatisfaction,
            ProviderResponse: selectedRowData?.providerResponse,
            ProviderEvidenceFiles: []
        };

        const submitQuery = () => {
            setSelectedRowData(null);
            setShowQueryFormPopup(false);
            setDataLoaded(false);
            getQueryTable();
        };

        return (
            <>
                <Modal show={showQueryFormPopup} onHide={onClose} className="query-modal" size="lg" centered>
                    <Modal.Header closeButton closeVariant="white">
                        <Modal.Title></Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <QueryForm
                            initialReadOnlyData={ReadOnlyData}
                            userData={UserData}
                            editData={existingQueryData}
                            onSubmit={submitQuery}
                            updateMode={selectedRowData?.editable && !selectedRowData?.resolutionContent?.includes("Closed") ? 0 : -1}
                        />
                    </Modal.Body>
                </Modal>
            </>
        );
    }

    const editCellRenderer = ({ cellData, rowData }) => {
        if (rowData.editable && !rowData.resolutionContent?.includes("Closed")) {
            return (
                <CustomTooltip content="Edit Query">
                    <Button
                        className="btn-table-action"
                        onClick={() => openQueryFormModal(rowData)}
                    >
                        <MdEdit color="blue" size={14} />
                    </Button>
                </CustomTooltip>
            );
        } else if (rowData.resolutionContent?.includes("Closed")) {
            return (
                <CustomTooltip content="View Resolved Query">
                    <Button
                        className="btn-table-action"
                        onClick={() => openQueryFormModal(rowData)}
                    >
                        <MdVisibility color="white" size={14} />
                    </Button>
                </CustomTooltip>
            );
        } else {
            return (
                <CustomTooltip content="View Only">
                    <Button
                        className="btn-table-action"
                        onClick={() => openQueryFormModal(rowData)}
                    >
                        <MdVisibility color="white" size={14} />
                    </Button>
                </CustomTooltip>
            );
        }
    };

    const dateCellRenderer = ({ cellData }) => {
        return <div>{formatShortDate(cellData)}</div>;
    };

    const floatCellRenderer = ({ cellData }) => {
        return <div>&pound;{cellData.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}</div>;
    };

    const filteredToggledData = useMemo(() => {
        if (toggleStatus === 'open') {
            return sortedData.filter(
                (rowData) => !rowData.resolutionContent?.includes('Closed')
            );
        } else {
            return sortedData.filter(
                (rowData) => rowData.resolutionContent?.includes('Closed')
            );
        }
    }, [sortedData, toggleStatus]);

    const exportToCSV = () => {
        const formatCurrencyForCSV = (value) => {
            if (value === null || value === undefined) return '';
            return `£${value.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
        };

        const columnMapping = [
            { key: 'id', label: 'Query ID' },
            { key: 'lineID', label: 'Line ID' },
            { key: 'notes', label: 'Reference' },
            { key: 'dateLogged', label: 'Date Logged', formatter: formatShortDate },
            { key: 'querytype', label: 'Query Type' },
            { key: 'querycategory', label: 'Query Category' },
            { key: 'sitename', label: 'Site Name' },
            { key: 'siteref', label: 'Site Ref' },
            { key: 'raisedby', label: 'Raised By' },
            { key: 'roledepartment', label: 'Role Department' },
            { key: 'rolename', label: 'Staff Name' },
            { key: 'dateOfJob', label: 'Date Of Job', formatter: formatShortDate },
            { key: 'hrsBilled', label: 'Hours Billed' },
            { key: 'rateBilled', label: 'Rate Billed', formatter: formatCurrencyForCSV },
            { key: 'billednett', label: 'Billed Nett', formatter: formatCurrencyForCSV },
            { key: 'providerName', label: 'Provider Name' },
            { key: 'queryContent', label: 'Query Content' },
            { key: 'queryCorrectNett', label: 'Correct Nett', formatter: formatCurrencyForCSV },
            { key: 'queryCorrectHrs', label: 'Correct Hours' },
            { key: 'correctRate', label: 'Correct Rate', formatter: formatCurrencyForCSV },
            { key: 'dateResolved', label: 'Date Resolved', formatter: formatShortDate },
            { key: 'resolvedNett', label: 'Resolved Nett', formatter: formatCurrencyForCSV },
            { key: 'credited', label: 'Credited' },
            { key: 'resolutionContent', label: 'Resolution Content' },
            { key: 'resolutionType', label: 'Resolution Type' },
            { key: 'resolutionSatisfaction', label: 'Resolution Satisfaction' },
            { key: 'providerResponse', label: 'Provider Response' }
        ];

        const headers = columnMapping.map(col => col.label);
        const csvContent = [
            headers.join(','),
            ...filteredToggledData.map(row =>
                columnMapping.map(col => {
                    let value = row[col.key];
                    if (col.formatter && value !== null && value !== undefined) {
                        value = col.formatter(value);
                    }

                    if (value === null || value === undefined) {
                        value = '';
                    }

                    value = String(value).replace(/"/g, '""');
                    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                        value = `"${value}"`;
                    }
                    return value;
                }).join(',')
            )
        ].join('\n');

        const BOM = '\uFEFF';
        const csvWithBOM = BOM + csvContent;

        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `query-report-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

      useEffect(() => {
            if (resolutionTypeFilter && resolutionFilteredData.length > 0) {
                const allClosed = resolutionFilteredData.every(
                        (rowData) => rowData.resolutionContent?.includes('Closed')
                );
                const allOpen = resolutionFilteredData.every(
                (rowData) => !rowData.resolutionContent?.includes('Closed')
                );

                if (allClosed && toggleStatus === 'open') {
                    setToggleStatus('closed');
                }
                else if (allOpen && toggleStatus === 'closed') {
                    setToggleStatus('open');
                }
            }
      }, [resolutionTypeFilter, resolutionFilteredData, toggleStatus]);

    useEffect(() => {
        if (queryIdToEdit && filteredToggledData.length > 0) {
            const matchingQuery = filteredToggledData.find(query => query.id === queryIdToEdit);
            if (matchingQuery) {
                openQueryFormModal(matchingQuery);
                setQueryIdToEdit(null);

                const urlParams = new URLSearchParams(window.location.search.toLowerCase());
                urlParams.delete('QueryID');
                const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
                window.history.replaceState({}, '', newUrl);
            }
        }
    }, [queryIdToEdit, filteredToggledData]);

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '42px',
            fontSize: '14px',
            borderColor: 'rgba(255,255,255,0.12)',
            backgroundColor: 'rgba(255,255,255,0.03)',
            borderRadius: '6px',
            boxShadow: 'none',
            '&:hover': { borderColor: 'rgba(255,255,255,0.25)' }
        }),
        singleValue: (base) => ({ ...base, color: 'white' }),
        placeholder: (base) => ({ ...base, color: 'rgba(255,255,255,0.78)' }),
        input: (base) => ({ ...base, color: 'white' }),
        menu: (base) => ({ ...base, zIndex: 9999, backgroundColor: '#1e163c', border: '1px solid rgba(255,255,255,0.12)' }),
        option: (base, state) => ({
            ...base,
            fontSize: '14px',
            backgroundColor: state.isSelected ? '#CB2C84' : state.isFocused ? 'rgba(0,93,229,0.1)' : 'transparent',
            color: 'white',
            '&:active': { backgroundColor: 'rgba(0,93,229,0.2)' }
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({ ...base, color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }),
        clearIndicator: (base) => ({ ...base, color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }),
    };

    const clearAllFilters = () => {
        setRoleDepartmentFilter('');
        setResolutionTypeFilter('');
        setSupplierFilter('');
        setSearchTerm('');
        const searchInput = document.querySelector('.query-filters-container .table-search');
        if (searchInput) searchInput.value = '';
    };

    const departmentOptions = uniqueRoleDepartments.map(dept => ({ value: dept, label: dept }));
    const resolutionTypeOptions = resolutionTypes.map(type => ({ value: type.id.toString(), label: type.resolution }));
    const supplierOptions = uniqueSuppliers.map(supplier => ({ value: supplier, label: supplier }));

    const showCol = (key) => !visibleColumns || visibleColumns.includes(key);

    return (
        <div className="query-table-root">
            <LoadingSpinner loading={loading} />
            {showQueryFormPopup && (
                <QueryFormModal onClose={() => setShowQueryFormPopup(false)} />
            )}
            {showModal && (
                <DownloadModal
                    links={downloadLinks}
                    onClose={() => setShowModal(false)}
                />
            )}
            <CustomToast
                show={showToast}
                onClose={() => setShowToast(false)}
                title={toastProps.title}
                bodyText={toastProps.bodyText}
                status={toastProps.status}
                delay={toastProps.delay}
                autohide={toastProps.autohide}
                position={toastProps.position}
            />

            {!isMobile && <div className="query-filters-container">
                <div className="query-filters-left">
                    <div className="filter-item">
                        <label>Department</label>
                        <Select
                            value={roleDepartmentFilter ? departmentOptions.find(o => o.value === roleDepartmentFilter) : null}
                            onChange={(selected) => setRoleDepartmentFilter(selected ? selected.value : '')}
                            options={departmentOptions}
                            placeholder="Select"
                            isClearable
                            isSearchable
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                        />
                    </div>
                    <div className="filter-item">
                        <label>Resolution Types</label>
                        <Select
                            value={resolutionTypeFilter ? resolutionTypeOptions.find(o => o.value === resolutionTypeFilter) : null}
                            onChange={(selected) => setResolutionTypeFilter(selected ? selected.value : '')}
                            options={resolutionTypeOptions}
                            placeholder="Select"
                            isClearable
                            isSearchable
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                        />
                    </div>
                    <div className="filter-item">
                        <label>Suppliers</label>
                        <Select
                            value={supplierFilter ? supplierOptions.find(o => o.value === supplierFilter) : null}
                            onChange={(selected) => setSupplierFilter(selected ? selected.value : '')}
                            options={supplierOptions}
                            placeholder="Select"
                            isClearable
                            isSearchable
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                        />
                    </div>
                    <button className="btn-clear-all" type="button" onClick={clearAllFilters}>
                        Clear All
                    </button>
                    <button className="btn-export-csv" type="button" onClick={exportToCSV}>
                        <FaFileDownload />
                        Export CSV
                    </button>
                </div>
                <div className="search-filter-item" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <div>
                        <label>Search</label>
                        <input
                            type="text"
                            placeholder="Free Search"
                            className="table-search"
                            onChange={handleSearchChange}
                        />
                    </div>
                    {columnConfig && visibleColumns && onColumnsChange && (
                        <ColumnToggle
                            columns={columnConfig}
                            visibleKeys={visibleColumns}
                            onChange={onColumnsChange}
                        />
                    )}
                </div>
            </div>}

            {isMobile ? (
                <div className="query-cards-container">
                    {filteredToggledData.length === 0 ? (
                        <div className="no-results">
                            <p>No queries found.</p>
                        </div>
                    ) : (
                        filteredToggledData.map((rowData, index) => (
                            <QueryTableCard
                                key={rowData.id || index}
                                rowData={rowData}
                                onDownloadClick={handleDownloadClick}
                                onEditClick={openQueryFormModal}
                            />
                        ))
                    )}
                </div>
            ) : (
                <div className="query-table-container">
                    <AutoSizer key={`table-${filteredToggledData.length}-${dataLoaded}`}>
                        {({ height }) => (
                            <Table
                                width={2200}
                                height={height}
                                horizontal={true}
                                scrollingResetTimeInterval={0}
                                rowHeight={44}
                                rowRender={50}
                                rowCount={filteredToggledData.length}
                                rowGetter={({ index }) => filteredToggledData[index]}
                                sort={handleSort}
                                className='container'
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                rowStyle={({ index }) => rowHighlightStyle(index)}
                                rowClassName={({ index }) => `queryTableRow ${index % 2 === 0 ? 'queryTableRow--odd' : 'queryTableRow--even'}`}
                            >
                                <Column width={35} minWidth={35} maxWidth={35} flexGrow={1} flexShrink={1} label="" dataKey="querycategory" cellRenderer={queryCategoryCellRenderer} headerRenderer={headerRenderer} />
                                <Column width={55} minWidth={55} maxWidth={55} flexGrow={1} flexShrink={1} label="" dataKey="editable" cellRenderer={editCellRenderer} />
                                <Column width={60} minWidth={60} maxWidth={60} label="Evidence" dataKey="uRefForAzureImages" cellRenderer={downloadCellRenderer} headerRenderer={headerRenderer} />
                                {showCol('id') && <Column width={60} minWidth={60} maxWidth={60} label="QueryID" dataKey="id" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('lineID') && <Column width={60} minWidth={60} maxWidth={60} label="LineID" dataKey="lineID" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('notes') && <Column width={120} minWidth={120} maxWidth={120} label="Reference" dataKey="notes" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('dateLogged') && <Column width={75} minWidth={75} maxWidth={75} label="Date Logged" dataKey="dateLogged" cellRenderer={dateCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('querytype') && <Column width={90} minWidth={90} maxWidth={90} label="Query Type" dataKey="querytype" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('sitename') && <Column width={130} minWidth={130} maxWidth={130} label="Site Name" dataKey="sitename" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('raisedby') && <Column width={130} minWidth={130} maxWidth={130} label="Raised by" dataKey="raisedby" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('roledepartment') && <Column width={95} minWidth={95} maxWidth={95} label="Role Department" dataKey="roledepartment" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('dateOfJob') && <Column width={75} minWidth={75} maxWidth={75} label="Date Of Job" dataKey="dateOfJob" cellRenderer={dateCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('hrsBilled') && <Column width={50} minWidth={50} maxWidth={50} label="Hours" dataKey="hrsBilled" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('rateBilled') && <Column width={70} minWidth={70} maxWidth={70} label="Rate" dataKey="rateBilled" cellRenderer={floatCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('providerName') && <Column width={130} minWidth={130} maxWidth={130} label="Provider Name" dataKey="providerName" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('queryContent') && <Column width={600} minWidth={600} label="Query Content" dataKey="queryContent" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('billednett') && <Column width={60} minWidth={60} maxWidth={60} label="Billed Nett" dataKey="billednett" cellRenderer={floatCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('queryCorrectNett') && <Column width={60} minWidth={60} maxWidth={60} label="Correct Nett" dataKey="queryCorrectNett" cellRenderer={floatCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('queryCorrectHrs') && <Column width={50} minWidth={50} maxWidth={50} label="Correct Hours" dataKey="queryCorrectHrs" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('dateResolved') && <Column width={70} minWidth={70} maxWidth={70} label="Date Resolved" dataKey="dateResolved" cellRenderer={dateCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('resolvedNett') && <Column width={60} minWidth={60} maxWidth={60} label="Resolved Nett" dataKey="resolvedNett" cellRenderer={floatCellRenderer} headerRenderer={headerRenderer} />}
                                {showCol('credited') && <Column width={60} minWidth={60} maxWidth={60} label="Credited" dataKey="credited" cellRenderer={defaultCellRenderer} headerRenderer={headerRenderer} />}
                            </Table>
                        )}
                    </AutoSizer>
                </div>
            )}

        </div>
    )
}

export default QueryTable;